/**
 * specDocumentService - Adapter layer for backward compatibility
 *
 * This service now delegates to artifactService for documents.
 * All SpecDocuments have been migrated to Artifacts with artifact_type='document'.
 *
 * This adapter exists for backward compatibility with existing components.
 * New code should use artifactService directly.
 */

import {
  artifactService,
  type Artifact,
  type DocumentCategory,
  type DocumentStatus,
  type DocumentOutlineItem,
} from './artifactService';

// Re-export types with old names for compatibility
export type SpecDocumentCategory = DocumentCategory;
export type SpecDocumentStatus = DocumentStatus;
export type SpecDocumentOutlineItem = DocumentOutlineItem;

// SpecDocument interface that maps to Artifact with document fields
export interface SpecDocument {
  id: string;
  title: string;  // Maps to Artifact.name
  slug: string | null;
  category: SpecDocumentCategory | null;
  status: SpecDocumentStatus;  // Maps to Artifact.doc_status
  content: string | null;
  summary: string | null;
  tags: string[];
  outline: SpecDocumentOutlineItem[] | string[] | null;
  metadata: Record<string, unknown>;
  version: number;
  checksum: string | null;
  ai_generated: boolean;
  ai_model: string | null;
  ai_context: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
  // Confirmation fields
  confirmed: boolean;
  confirmed_at: string | null;
  confirmed_by_name: string | null;
  confirmation_note: string | null;
  requires_confirmation: boolean;
  // Computed attributes
  word_count: number | null;
  reading_time_minutes: number | null;
  content_preview: string | null;
  created_by_name: string | null;
  last_modified_by_name: string | null;
  is_published: boolean;
  can_edit: boolean;
  can_approve: boolean;
  has_embeddings: boolean;
  needs_embedding: boolean;
  embedding_count: number;
  category_display: string | null;
}

// Form data interface
export interface SpecDocumentFormData {
  title: string;
  category: SpecDocumentCategory;
  content?: string;
  summary?: string;
  tags?: string[];
  metadata?: Record<string, unknown>;
}

// List response interface
interface SpecDocumentListResponse {
  data: SpecDocument[];
  meta: {
    current_page: number;
    total_pages: number;
    total_count: number;
  };
}

// Filters interface
interface SpecDocumentFilters {
  category?: SpecDocumentCategory;
  status?: SpecDocumentStatus;
  search?: string;
  tag?: string;
  ai_generated?: boolean;
}

// Helper function to convert Artifact to SpecDocument
function artifactToSpecDocument(artifact: Artifact): SpecDocument {
  return {
    id: artifact.id,
    title: artifact.name,
    slug: artifact.slug,
    category: artifact.category,
    status: artifact.doc_status || 'draft',
    content: artifact.content,
    summary: artifact.summary,
    tags: artifact.tags || [],
    outline: artifact.outline,
    metadata: artifact.metadata,
    version: artifact.version,
    checksum: artifact.checksum,
    ai_generated: artifact.ai_generated,
    ai_model: artifact.ai_model,
    ai_context: artifact.ai_context,
    created_at: artifact.created_at,
    updated_at: artifact.updated_at,
    // Confirmation fields
    confirmed: artifact.confirmed ?? false,
    confirmed_at: artifact.confirmed_at ?? null,
    confirmed_by_name: artifact.confirmed_by_name ?? null,
    confirmation_note: artifact.confirmation_note ?? null,
    requires_confirmation: artifact.requires_confirmation ?? false,
    // Computed attributes
    word_count: artifact.word_count,
    reading_time_minutes: artifact.reading_time_minutes,
    content_preview: artifact.content_preview,
    created_by_name: artifact.created_by_name,
    last_modified_by_name: artifact.last_modified_by_name,
    is_published: artifact.is_published,
    can_edit: artifact.can_edit,
    can_approve: false, // Not tracked in artifact
    has_embeddings: artifact.has_embeddings,
    needs_embedding: artifact.needs_embedding,
    embedding_count: artifact.embedding_count,
    category_display: artifact.category_display,
  };
}

export const specDocumentService = {
  // Get all spec documents for a project
  async getDocuments(
    projectId: string,
    filters: SpecDocumentFilters = {},
    page = 1,
    perPage = 20
  ): Promise<SpecDocumentListResponse> {
    const artifactFilters: {
      category?: DocumentCategory;
      doc_status?: DocumentStatus;
      search?: string;
      tag?: string;
      ai_generated?: boolean;
    } = {};

    if (filters.category) artifactFilters.category = filters.category;
    if (filters.status) artifactFilters.doc_status = filters.status;
    if (filters.search) artifactFilters.search = filters.search;
    if (filters.tag) artifactFilters.tag = filters.tag;
    if (filters.ai_generated !== undefined) artifactFilters.ai_generated = filters.ai_generated;

    const response = await artifactService.getDocuments(projectId, artifactFilters, page, perPage);
    return {
      data: response.data.map(artifactToSpecDocument),
      meta: response.meta,
    };
  },

  // Get a single document
  async getDocument(projectId: string, documentId: string): Promise<SpecDocument> {
    const artifact = await artifactService.getArtifact(projectId, documentId);
    return artifactToSpecDocument(artifact);
  },

  // Get document by slug
  async getDocumentBySlug(projectId: string, slug: string): Promise<SpecDocument> {
    const artifact = await artifactService.getDocumentBySlug(projectId, slug);
    return artifactToSpecDocument(artifact);
  },

  // Create a new document
  async createDocument(projectId: string, data: SpecDocumentFormData): Promise<SpecDocument> {
    const artifact = await artifactService.createArtifact(projectId, {
      name: data.title,
      artifact_type: 'document',
      content: data.content,
      category: data.category,
      tags: data.tags,
      metadata: data.metadata,
    });
    return artifactToSpecDocument(artifact);
  },

  // Create document from template
  async createFromTemplate(
    projectId: string,
    category: SpecDocumentCategory
  ): Promise<SpecDocument> {
    const artifact = await artifactService.createDocumentFromTemplate(projectId, category);
    return artifactToSpecDocument(artifact);
  },

  // Update a document
  async updateDocument(
    projectId: string,
    documentId: string,
    data: Partial<SpecDocumentFormData>
  ): Promise<SpecDocument> {
    const updateData: Parameters<typeof artifactService.updateArtifact>[2] = {};
    if (data.title !== undefined) updateData.name = data.title;
    if (data.content !== undefined) updateData.content = data.content;
    if (data.category !== undefined) updateData.category = data.category;
    if (data.tags !== undefined) updateData.tags = data.tags;
    if (data.metadata !== undefined) updateData.metadata = data.metadata;

    const artifact = await artifactService.updateArtifact(projectId, documentId, updateData);
    return artifactToSpecDocument(artifact);
  },

  // Delete a document
  async deleteDocument(projectId: string, documentId: string): Promise<void> {
    await artifactService.deleteArtifact(projectId, documentId);
  },

  // Update document status
  async updateStatus(
    projectId: string,
    documentId: string,
    status: SpecDocumentStatus
  ): Promise<SpecDocument> {
    const artifact = await artifactService.updateDocumentStatus(projectId, documentId, status);
    return artifactToSpecDocument(artifact);
  },

  // Submit for review
  async submitForReview(projectId: string, documentId: string): Promise<SpecDocument> {
    return this.updateStatus(projectId, documentId, 'review');
  },

  // Approve document
  async approveDocument(projectId: string, documentId: string): Promise<SpecDocument> {
    return this.updateStatus(projectId, documentId, 'approved');
  },

  // Deprecate document
  async deprecateDocument(projectId: string, documentId: string): Promise<SpecDocument> {
    return this.updateStatus(projectId, documentId, 'deprecated');
  },

  // Duplicate a document
  async duplicateDocument(
    projectId: string,
    documentId: string,
    newTitle?: string
  ): Promise<SpecDocument> {
    const artifact = await artifactService.duplicateArtifact(projectId, documentId, {
      name: newTitle,
    });
    return artifactToSpecDocument(artifact);
  },

  // Generate content with AI (not implemented in artifact service yet)
  async generateContent(
    projectId: string,
    documentId: string,
    _options?: {
      regenerate?: boolean;
      requirements?: string[];
      constraints?: string[];
      context?: Record<string, unknown>;
    }
  ): Promise<SpecDocument> {
    // For now, just return the current document
    // AI generation would need to be implemented
    return this.getDocument(projectId, documentId);
  },

  // Generate embeddings (not directly available, needs backend implementation)
  async generateEmbeddings(projectId: string, documentId: string): Promise<SpecDocument> {
    // For now, just return the current document
    return this.getDocument(projectId, documentId);
  },

  // Export document as markdown
  async exportMarkdown(projectId: string, documentId: string): Promise<string> {
    const doc = await this.getDocument(projectId, documentId);
    return doc.content || '';
  },

  // Export document as JSON
  async exportJson(projectId: string, documentId: string): Promise<object> {
    const doc = await this.getDocument(projectId, documentId);
    return doc;
  },

  // Tag management - implemented through update
  async addTag(projectId: string, documentId: string, tag: string): Promise<SpecDocument> {
    const doc = await this.getDocument(projectId, documentId);
    const newTags = [...(doc.tags || []), tag.toLowerCase().trim()];
    return this.updateDocument(projectId, documentId, { tags: newTags });
  },

  async removeTag(projectId: string, documentId: string, tag: string): Promise<SpecDocument> {
    const doc = await this.getDocument(projectId, documentId);
    const newTags = (doc.tags || []).filter((t) => t !== tag.toLowerCase().trim());
    return this.updateDocument(projectId, documentId, { tags: newTags });
  },

  // === Helper Methods ===

  // Get all categories with display names
  getCategories(): Array<{ value: SpecDocumentCategory; label: string }> {
    return artifactService.getDocumentCategoriesWithLabels();
  },

  // Get status badge color
  getStatusColor(status: SpecDocumentStatus): string {
    return artifactService.getDocumentStatusColor(status);
  },

  // Get category icon
  getCategoryIcon(category: SpecDocumentCategory): string {
    return artifactService.getDocumentCategoryIcon(category);
  },
};

export default specDocumentService;
